import { ArrowRight, TriangleAlert } from "lucide-react";
import { useNavigate } from "react-router";
import {
  alertStatusText,
  alertTagLabel,
  type ComplianceAlert,
} from "@/hooks/use-site";
import { cn } from "@/lib/utils";

/** Missing and expired are red; anything merely expiring is amber. */
function isRed(alert: ComplianceAlert): boolean {
  return alert.status === "missing" || alert.status === "expired";
}

/**
 * The dark-headed alert card at the foot of Today. The whole card is one
 * button: tapping anywhere on it opens the roster filtered to the flags.
 *
 * The destination is relative — `crew`, not `/crew`. React Router resolves it
 * against the route that renders this, which is under `/projects/:projectId`;
 * the absolute form left the project entirely and bounced off the catch-all
 * back to the picker.
 */
export function ComplianceBanner({ alerts }: { alerts: ComplianceAlert[] }) {
  const navigate = useNavigate();
  if (alerts.length === 0) return null;

  return (
    <button
      type="button"
      onClick={() => void navigate("crew?filter=missing")}
      className="block w-full cursor-pointer overflow-hidden rounded-card-lg border-[1.5px] border-border bg-surface p-0 text-left shadow-card"
    >
      <div className="flex items-center gap-[9px] bg-chrome px-[22px] py-4">
        <span className="flex text-brand-bright">
          <TriangleAlert size={17} />
        </span>
        <span className="font-display text-[0.7rem] font-semibold tracking-[0.1em] text-white uppercase">
          Compliance needs attention
        </span>
        <div className="flex-1" />
        <span className="flex items-center gap-[5px] font-display text-[0.74rem] font-bold tracking-[0.05em] text-brand-soft uppercase">
          Review {alerts.length}
          <ArrowRight size={15} />
        </span>
      </div>

      <div className="flex flex-col">
        {alerts.map((a) => (
          <div
            key={`${a.personId}-${a.label}`}
            className={cn(
              "flex items-center gap-[13px] border-b border-l-4 border-border-faint py-[13px] pr-[22px] pl-[18px]",
              isRed(a) ? "border-l-danger" : "border-l-warn",
            )}
          >
            <span
              className={cn(
                "rounded-tag px-[7px] py-[3px] font-display text-[0.62rem] font-bold tracking-[0.06em] whitespace-nowrap text-white uppercase",
                isRed(a) ? "bg-danger" : "bg-warn",
              )}
            >
              {alertTagLabel(a.status)}
            </span>
            <span className="min-w-0 flex-1 text-[0.9rem]">
              <span className="font-bold text-ink">{a.personName}</span>
              <span className="text-faint"> · {a.label}</span>
            </span>
            <span className="text-[0.8rem] font-semibold whitespace-nowrap text-muted">
              {alertStatusText(a)}
            </span>
          </div>
        ))}
      </div>
    </button>
  );
}
